import { useCallback, type ReactNode } from "react";
import { Tooltip, message } from "antd";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../../utils/avatarDisplayWord";
import { SPACE_HOME_HERO_LAYOUT } from "../Config";
import type { SpaceProfile } from "../SpaceType";
import { formatSpaceMetricValue } from "../spacePageUtils";
import { SpaceCertLabelTag } from "./SpaceCertLabelTag";

/** 空间头像、昵称、简介与指标区（与空间首页 Hero 一致，不含操作按钮） */
export function SpaceProfileHero(props: {
  profile: SpaceProfile;
  className?: string;
  actions?: ReactNode;
}): JSX.Element {
  const { profile, className, actions } = props;
  const certLabel = profile.certLabelName?.trim() ?? "";
  const showCertLabel = certLabel !== "";
  const introText = profile.signature?.trim() ?? "";
  const { avatar } = SPACE_HOME_HERO_LAYOUT;

  const onCopySpaceId = useCallback(() => {
    void navigator.clipboard.writeText(profile.spaceId).then(
      () => {
        message.success("已复制空间ID");
      },
      () => {
        message.error("复制失败，请重试");
      }
    );
  }, [profile.spaceId]);

  const heroClassName = className
    ? `space-home-hero ${className}`
    : "space-home-hero";

  return (
    <div className={heroClassName}>
      <div className="space-home-avatar-wrap">
        <EasyAvatar
          shape="circle"
          size={avatar.size}
          url={profile.avatarUrl?.trim() || undefined}
          word={avatarDisplayWord(profile.name, profile.spaceId)}
          alt=""
          className="space-home-avatar"
          border={{ width: avatar.border.width, color: avatar.border.color }}
        />
      </div>
      <div className="space-home-titles">
        <div className="space-home-row space-home-row--name">
          <span className="space-home-name-text">{profile.name}</span>
          {showCertLabel ? <SpaceCertLabelTag name={certLabel} /> : null}
        </div>
        {introText !== "" ? (
          <p className="space-home-intro">{introText}</p>
        ) : null}
        <div className="space-home-metrics" aria-label="空间数据">
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <span className="space-home-metrics-value">
                {formatSpaceMetricValue(profile.followerCount)}
              </span>
            </div>
            <span className="space-home-metrics-label">粉丝</span>
          </div>
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <Tooltip title="复制">
                <span
                  className="space-home-metrics-value space-home-metrics-value--copyable"
                  translate="no"
                  role="button"
                  tabIndex={0}
                  onClick={onCopySpaceId}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onCopySpaceId();
                    }
                  }}
                >
                  {profile.spaceId}
                </span>
              </Tooltip>
            </div>
            <span className="space-home-metrics-label">龙粉号</span>
          </div>
          <div className="space-home-metrics-col">
            <div className="space-home-metrics-value-row">
              <span className="space-home-metrics-value">
                {formatSpaceMetricValue(profile.subscriptionCount ?? 0)}
              </span>
            </div>
            <span className="space-home-metrics-label">订阅</span>
          </div>
        </div>
        {actions}
      </div>
    </div>
  );
}
